import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import type { AuthState, LoginCredentials } from '../types';
import api from '../services/api';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasRole: (role: string | string[]) => boolean;
  hasFeature: (featureCode: string) => boolean;
  availableFeatures: string[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const [availableFeatures, setAvailableFeatures] = useState<string[]>([]);

  const checkAuth = useCallback(async () => {
    const token = api.getToken();
    
    if (!token) {
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
      return;
    }

    try {
      const response = await api.getCurrentUser();
      
      if (response.success && response.data) {
        setState({
          user: response.data,
          token,
          isAuthenticated: true,
          isLoading: false,
        });

        // Load available features for clients
        if (response.data.role === 'client') {
          const featuresResponse = await api.getAvailableFeatures();
          if (featuresResponse.success && featuresResponse.data) {
            setAvailableFeatures(featuresResponse.data);
          }
        } else {
          // Admins and resellers have access to all features for viewing
          setAvailableFeatures(['ALL']);
        }
      } else {
        // Token invalid, try refresh
        const refreshResponse = await api.refreshToken();
        
        if (refreshResponse.success && refreshResponse.data) {
          setState({
            user: refreshResponse.data.user,
            token: refreshResponse.data.token,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          api.setToken(null);
          setState({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
          });
        }
      }
    } catch (error) {
      api.setToken(null);
      setState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      });
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> => {
    setState(prev => ({ ...prev, isLoading: true }));

    const response = await api.login(credentials);

    if (response.success && response.data) {
      const { user, token, refreshToken } = response.data;
      
      localStorage.setItem('refresh_token', refreshToken);
      
      setState({
        user,
        token,
        isAuthenticated: true,
        isLoading: false,
      });

      // Load features for clients
      if (user.role === 'client') {
        const featuresResponse = await api.getAvailableFeatures();
        if (featuresResponse.success && featuresResponse.data) {
          setAvailableFeatures(featuresResponse.data);
        }
      } else {
        setAvailableFeatures(['ALL']);
      }

      return { success: true };
    }

    setState(prev => ({ ...prev, isLoading: false }));
    return { success: false, error: response.error };
  };

  const logout = async () => {
    await api.logout();
    localStorage.removeItem('refresh_token');
    setAvailableFeatures([]);
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const hasRole = (role: string | string[]): boolean => {
    if (!state.user) return false;
    const roles = Array.isArray(role) ? role : [role];
    return roles.includes(state.user.role);
  };

  const hasFeature = (featureCode: string): boolean => {
    if (!state.user) return false;
    if (state.user.role === 'admin') return true;
    if (availableFeatures.includes('ALL')) return true;
    return availableFeatures.includes(featureCode);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        checkAuth,
        hasRole,
        hasFeature,
        availableFeatures,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Protected Route wrapper component
interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
  requiredFeature?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  requiredFeature,
}) => {
  const { isAuthenticated, isLoading, user, hasRole, hasFeature } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    window.location.href = '/login';
    return null;
  }

  if (user.status !== 'active') {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-4">
        <div className="bg-[#121212] border border-red-900/50 rounded-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">⚠️</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Account {user.status}</h2>
          <p className="text-neutral-400 mb-6">
            {user.status === 'pending'
              ? 'Your account is pending approval. Please wait for admin to activate your account.'
              : user.status === 'suspended'
              ? 'Your account has been suspended. Please contact support.'
              : 'Your account is currently inactive.'}
          </p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-6 py-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (allowedRoles && !hasRole(allowedRoles)) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-4">
        <div className="bg-[#121212] border border-yellow-900/50 rounded-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🔒</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-neutral-400 mb-6">
            You don't have permission to access this page.
          </p>
          <button
            onClick={() => window.history.back()}
            className="px-6 py-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (requiredFeature && !hasFeature(requiredFeature)) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-4">
        <div className="bg-[#121212] border border-blue-900/50 rounded-2xl p-8 max-w-md text-center">
          <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">💎</span>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Feature Locked</h2>
          <p className="text-neutral-400 mb-6">
            This feature is not available in your current subscription plan.
            Upgrade to access this feature.
          </p>
          <button
            onClick={() => window.location.href = '/client/subscription'}
            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
          >
            View Plans
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default AuthContext;
